import { z } from 'zod';
import { supabase } from '@/utils/supabase';
import type {
  AppraisalPaymentLinkLoanSearch,
  AppraisalPaymentLinksReport,
  AppraisalPaymentReminderOutcome,
  AppraisalPaymentReminderPreview,
  AppraisalSnapshot,
  AppraisalUnresolvedPaymentLink
} from '../../supabase/functions/_shared/appraisal-workspace-contract';

export interface AppraisalLoanIdentity {
  loanId: string;
  encompassLoanId: string;
}
export class AppraisalWorkspaceError extends Error {
  constructor(
    message: string,
    public status?: number,
    /** The function's error body, e.g. the current recipients when they changed. */
    public details?: Record<string, unknown> | null
  ) {
    super(message);
    this.name = 'AppraisalWorkspaceError';
  }
}

async function invoke<T>(
  loan: AppraisalLoanIdentity,
  action: string,
  values: Record<string, unknown> = {}
): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>('appraisal-workspace', {
    body: { ...values, ...loan, action }
  });
  if (error) {
    const response = 'context' in error && error.context instanceof Response ? error.context : null;
    const details = response
      ? ((await response
          .clone()
          .json()
          .catch(() => null)) as ({ error?: string } & Record<string, unknown>) | null)
      : null;
    throw new AppraisalWorkspaceError(
      details?.error || error.message || 'The appraisal request failed.',
      response?.status,
      details
    );
  }
  if (data === null || data === undefined) {
    throw new AppraisalWorkspaceError('The appraisal service returned no information.');
  }
  return data;
}

export function getAppraisalSnapshot(loan: AppraisalLoanIdentity): Promise<AppraisalSnapshot> {
  return invoke(loan, 'snapshot');
}
export async function refreshAppraisalWorkspace(loan: AppraisalLoanIdentity): Promise<void> {
  await invoke(loan, 'refresh');
}
export async function runAppraisalAudit(loan: AppraisalLoanIdentity, reportId: string): Promise<void> {
  await invoke(loan, 'audit', { reportId });
}
export async function rerunAppraisalFeeAudit(loan: AppraisalLoanIdentity): Promise<void> {
  const result = await invoke<{ queued?: boolean }>(loan, 'rerun_fee_audit');
  if (result.queued !== true) {
    throw new AppraisalWorkspaceError('The fee audit could not be queued. Try again.');
  }
}
export async function resolveAppraisalXml(loan: AppraisalLoanIdentity, reportId: string, note: string): Promise<void> {
  await invoke(loan, 'resolve', { reportId, note });
}

export async function uploadAppraisalXml(loan: AppraisalLoanIdentity, file: File, reportId?: string): Promise<void> {
  if (!/\.xml$/i.test(file.name) || file.size === 0 || file.size > 64 * 1024 * 1024) {
    throw new AppraisalWorkspaceError('Choose a nonempty XML file up to 64 MiB.');
  }
  const prepared = await invoke<{ reportId: string; path: string; token: string }>(loan, 'prepare_upload', {
    reportId,
    filename: file.name,
    bytes: file.size
  });
  const { error } = await supabase.storage
    .from('appraisal-source')
    .uploadToSignedUrl(prepared.path, prepared.token, file, { contentType: 'application/xml' });
  if (error) {
    throw new AppraisalWorkspaceError(`The XML could not be uploaded: ${error.message}`);
  }
  await invoke(loan, 'confirm_upload', { reportId: prepared.reportId, uploadPath: prepared.path });
}

function saveDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = Array.from(filename, (char) =>
    char.charCodeAt(0) < 32 || char === '/' || char === '\\' ? '_' : char
  ).join('');
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadAppraisalDocument(
  loan: AppraisalLoanIdentity,
  reportId: string,
  kind: 'pdf' | 'xml',
  options: { saveToDisk: false }
): Promise<Blob>;
export function downloadAppraisalDocument(
  loan: AppraisalLoanIdentity,
  reportId: string,
  kind: 'pdf' | 'xml'
): Promise<void>;
export async function downloadAppraisalDocument(
  loan: AppraisalLoanIdentity,
  reportId: string,
  kind: 'pdf' | 'xml',
  options: { saveToDisk?: boolean } = {}
): Promise<Blob | void> {
  const blob = await invoke<Blob>(loan, 'download', { reportId, kind });
  if (!(blob instanceof Blob)) {
    throw new AppraisalWorkspaceError('The appraisal download was not a document.');
  }
  if (options.saveToDisk !== false) {
    saveDownload(blob, `appraisal-${reportId}.${kind}`);
    return;
  }
  return blob;
}
export async function downloadAppraisalResource(
  loan: AppraisalLoanIdentity,
  orderId: string,
  resourceId: string,
  name: string
): Promise<void> {
  const blob = await invoke<Blob>(loan, 'download_resource', { orderId, resourceId });
  if (!(blob instanceof Blob)) {
    throw new AppraisalWorkspaceError('The provider download was not a document.');
  }
  saveDownload(blob, name);
}

export interface PaymentLinkReminderInput {
  requestId: string;
  attemptId: string;
  envelopeKey: string;
}
export interface AddPaymentLinkInput {
  url?: string;
  amcName?: string;
  amount?: string;
  fileId?: string;
  orderReferenceId?: string;
  observationId?: string;
  replaceRequestId?: string;
}

export function previewPaymentLinkReminder(
  loan: AppraisalLoanIdentity,
  requestId: string
): Promise<AppraisalPaymentReminderPreview> {
  return invoke(loan, 'send_payment_link_reminder', { requestId, preview: true });
}
export function sendPaymentLinkReminder(
  loan: AppraisalLoanIdentity,
  input: PaymentLinkReminderInput
): Promise<AppraisalPaymentReminderOutcome> {
  return invoke(loan, 'send_payment_link_reminder', { ...input });
}
/** Saves the link and returns the payment request it is now on (the same request when the link was already saved). */
export async function addPaymentLink(
  loan: AppraisalLoanIdentity,
  input: AddPaymentLinkInput
): Promise<{ requestId: string | null }> {
  const result = await invoke<{ requestId?: unknown }>(loan, 'add_payment_link', { ...input });
  return { requestId: typeof result.requestId === 'string' ? result.requestId : null };
}
export async function listUnresolvedPaymentLinks(loan: AppraisalLoanIdentity): Promise<AppraisalUnresolvedPaymentLink[]> {
  const result = await invoke<{ links?: AppraisalUnresolvedPaymentLink[] }>(loan, 'list_unresolved_payment_links');
  return Array.isArray(result.links) ? result.links : [];
}
export async function resolvePaymentLinkReminder(
  loan: AppraisalLoanIdentity,
  input: { attemptId: string; delivered: boolean; note: string }
): Promise<void> {
  await invoke(loan, 'resolve_payment_reminder', input);
}

const reportTime = z.string().min(1);
const paymentLinksReportSchema = z.object({
  enabled: z.boolean(),
  reminderEnabled: z.boolean(),
  truncated: z.boolean(),
  rows: z.array(
    z.object({
      id: z.string().uuid(),
      loanId: z.string().uuid(),
      encompassLoanId: z.string().min(1),
      loanNumber: z.string().min(1),
      loanFolder: z.string(),
      fundedDate: z.string().nullable(),
      borrowerName: z.string().nullable(),
      propertyStreet: z.string().nullable(),
      propertyLocality: z.string().nullable(),
      loanOfficerName: z.string().nullable(),
      amcName: z.string().nullable(),
      url: z.string().url(),
      host: z.string().min(1),
      reference: z.object({ fileId: z.string().optional(), orderReferenceId: z.string().optional() }),
      firstSeenAt: reportTime,
      reminderOpen: z.boolean(),
      lastReminder: z
        .object({
          id: z.string().uuid(),
          status: z.enum(['sending', 'sent', 'failed', 'unknown']),
          createdAt: reportTime,
          sentAt: z.string().nullable(),
          senderName: z.string().nullable()
        })
        .nullable()
    })
  )
});

/** Company Reports → Appraisal Processes → Payment links: current links the viewer could resend. */
export async function getPaymentLinksReport(): Promise<AppraisalPaymentLinksReport> {
  const { data, error } = await supabase.rpc('get_appraisal_payment_links_report');
  if (error) {
    throw new Error(error.code === '42501' ? 'You do not have access to this report.' : 'Payment links could not be loaded.');
  }
  const result = paymentLinksReportSchema.safeParse(data);
  if (!result.success) {
    throw new Error('Payment links could not be read. Please try again.');
  }
  return result.data;
}

const paymentLinkLoanSearchSchema = z.object({
  rows: z.array(
    z.object({
      loanId: z.string().uuid(),
      encompassLoanId: z.string().min(1),
      loanNumber: z.string().min(1),
      borrowerName: z.string().nullable(),
      propertyStreet: z.string().nullable(),
      propertyLocality: z.string().nullable(),
      currentLink: z
        .object({ id: z.string().uuid(), amcName: z.string().nullable(), host: z.string().min(1), reminderOpen: z.boolean() })
        .nullable()
    })
  ),
  more: z.boolean()
});

/** Add payment link in the report: loans the viewer could save a link to, matching a loan number, borrower or address. */
export async function searchPaymentLinkLoans(query: string): Promise<AppraisalPaymentLinkLoanSearch> {
  const { data, error } = await supabase.rpc('search_appraisal_payment_link_loans', { p_query: query });
  if (error) {
    throw new Error(error.code === '42501' ? 'You do not have access to this report.' : 'Loans could not be searched.');
  }
  const result = paymentLinkLoanSearchSchema.safeParse(data);
  if (!result.success) {
    throw new Error('Loans could not be read.');
  }
  return result.data;
}
